import { useCallback } from 'react';
import { addMessageFeedback, clearMessages, removeMessageFeedback, setConversation, setMessageFailure, setMessageSuccess,loadExistingMessageSuccess, setPendingMessage, setLoading, setTablePaginatedDataSet, setPdfDownloadLoading, setPdfDownloadError, addInsightToMessage, addStreamingStep, addVisualizationResult, stopStreaming, addReportSuggestions } from 'features/chat-slice';
import { setActiveConversation, setNewConversation } from 'features/conversation-slice';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {  RootState } from '../redux/store';
import { useAiChatApiService } from 'api/providers/AppServiceProvider';
import { Conversation, FeedbackRequest, StreamingResponse, MainResultResponse, InsightResponse, InsightsCompleteResponse } from 'models/ai-chat/ai-chat-response';
import { TableRequest } from 'models/ai-chat/table-pagination';
import { FilterItem } from 'models/ai-chat/filter-request';
import { parseStreamingLine, isWorkflowEvent, isStreamingResponse, extractStreamingStep, isToolResultEvent, extractToolResult } from 'utils/streaming-parser';

export const useChat = () => {
  const dispatch = useAppDispatch();
  const { 
    messages, 
    pendingMessage, 
    error, 
    currentConversationId,
    suggestions,
    suggestedQuestions,
    suggestedVisualizations, 
    loading,
    isDownloadingPDF,
    pdfDownloadError,
    pdfDownloadErrorMessageId
  } = useAppSelector((state: RootState) => state.chat);
  
  const aiChatApiService = useAiChatApiService();
  const { activeConversationId } = useAppSelector((state: RootState) => state.conversations);

  const sendChatMessage = useCallback(
    async (message: string) => {
      if (!message.trim()) {
        return;
      }
      
      // Check if this is an edit or regenerate message
      const editMatch = message.match(/^\[EDIT:([^\]]+)\]\s*(.+)$/);
      const regenerateMatch = message.match(/^\[REGENERATE:([^\]]+)\]\s*(.+)$/);
      const isEdit = !!editMatch;
      const isRegenerate = !!regenerateMatch;
      const actualMessage = isEdit ? editMatch![2] : isRegenerate ? regenerateMatch![2] : message;
      const editMessageId = isEdit ? editMatch![1] : isRegenerate ? regenerateMatch![1] : undefined;
      
      await dispatch(setPendingMessage({ 
        prompt: actualMessage, 
        isEdit: isEdit, 
        editMessageId: editMessageId,
        isRegenerate: isRegenerate
      }));
      
      if (typeof aiChatApiService.sendExplanationQueryRequest === 'function') {
        let accumulatedResponse = '';
        let conversationId = currentConversationId;
        let chunkCount = 0;
        const startTime = Date.now();
        
        // Set a timeout to clear pending state if streaming takes too long (5 minutes)
        const timeoutId = setTimeout(() => {
          dispatch(setMessageFailure('Request timeout - please try again'));
        }, 5 * 60 * 1000); // 5 minutes timeout
        
        const requestPayload = {
          prompt: actualMessage,
          message_id:isRegenerate ? editMessageId : undefined,
          conversation_id: currentConversationId || undefined,
          is_edited: isEdit,
          is_regenerate: isRegenerate
        };
        
        aiChatApiService.sendExplanationQueryRequest(requestPayload, 
        // onChunk - handle streaming chunks
        (chunk: any) => {
          chunkCount++;
          const currentTime = Date.now();
          const timeSinceStart = currentTime - startTime;
          const timestamp = new Date().toISOString();
          const timeWithMs = timestamp.split('T')[1].replace('Z', '');
          
          let parsedEvent = null;
          
          if (typeof chunk === 'string') {
            parsedEvent = parseStreamingLine(chunk);
            if (parsedEvent) {
              if (isWorkflowEvent(parsedEvent)) {
                const step = extractStreamingStep(parsedEvent);
                dispatch(addStreamingStep(step));
              } else if (isToolResultEvent(parsedEvent)) {
                const toolResult = extractToolResult(parsedEvent);
                if (toolResult) {
                  dispatch(addVisualizationResult(toolResult));
                }
              } else if (isStreamingResponse(parsedEvent)) {
                handleStreamingResponse(parsedEvent as StreamingResponse, conversationId, actualMessage);
              } else if ((parsedEvent as any).type === 'report_suggestions_result' && (parsedEvent as any).report_suggestions) {
                dispatch(addReportSuggestions({ reportSuggestions: (parsedEvent as any).report_suggestions }));
              } else if ((parsedEvent as any).type === 'clarification_response' && (parsedEvent as any).clarifications) {
                dispatch(addStreamingStep({
                  type: (parsedEvent as any).type,
                  step: (parsedEvent as any).step || 'clarification',
                  status: 'completed' as const,
                  message: '',
                  timestamp: (parsedEvent as any).timestamp || Date.now(),
                  details: parsedEvent,
                  context: undefined,
                }));
              }
            } else {
              accumulatedResponse += chunk;
            }
          } else if (typeof chunk === 'object' && chunk.type) {
            if (isWorkflowEvent(chunk)) {
              const step = extractStreamingStep(chunk);
              dispatch(addStreamingStep(step));
            } else if (isToolResultEvent(chunk)) {
              const toolResult = extractToolResult(chunk);
              if (toolResult) {
                dispatch(addVisualizationResult(toolResult));
              }
            } else if (isStreamingResponse(chunk)) {
              handleStreamingResponse(chunk as StreamingResponse, conversationId, actualMessage);
            } else if (chunk.type === 'report_suggestions_result' && chunk.report_suggestions) {
              dispatch(addReportSuggestions({ reportSuggestions: chunk.report_suggestions }));
            } else if (chunk.type === 'clarification_response' && chunk.clarifications) {
              dispatch(addStreamingStep({
                type: chunk.type,
                step: chunk.step || 'clarification',
                status: 'completed' as const,
                message: '',
                timestamp: chunk.timestamp || Date.now(),
                details: chunk,
                context: undefined,
              }));
            }
          }
        },
        // onError - handle errors
        (error: Error) => {
          console.error('Streaming error:', error);
          clearTimeout(timeoutId); // Clear timeout
          dispatch(setMessageFailure(error.message));
        },
        // onComplete - handle completion
        () => {
          clearTimeout(timeoutId); // Clear timeout
          
          // Clear pending state when streaming completes
          dispatch(setLoading(false));
          dispatch(stopStreaming()); // Clear pendingMessage flag
          
          // Ensure pending message is cleared
          if (chunkCount === 0) {
            // If no chunks were received, clear pending state
            dispatch(setMessageFailure('No response received from server'));
          }
          
          // No need to create a final response - the main_result and insights already handle message creation
          // The streaming responses (main_result, insight, insights_complete) already create/update the appropriate messages
        }
        ).catch((error: any) => {
          console.error('Explanation query error:', error);
          clearTimeout(timeoutId); // Clear timeout
          // Don't dispatch setMessageFailure here as it's already handled in onError callback
          // This prevents duplicate error messages
        });
      } else {
        // Fallback to regular query if explanation query is not available
        console.warn('sendExplanationQueryRequest not available, falling back to sendQueryRequest');
        aiChatApiService.sendQueryRequest({
          prompt: actualMessage,
          conversation_id: currentConversationId || undefined,
        }).then(
          (response: any) => {
            if(!currentConversationId){
              let conv :Conversation = {
                conversation_id: response.conversation_id,
                title: actualMessage || 'New Conversation',
                updated_at: new Date().toISOString(),
                latest_message: {
                  content: actualMessage,
                  role: 'user',
                  created_at: new Date().toISOString(),
                  sql_query: '',
                  feedback: null
                },
                created_at: new Date().toISOString()
              }
              dispatch(setNewConversation(conv));
            }
            dispatch(setMessageSuccess(response));
          },
          (error:any) => {
            console.error(error);
            dispatch(setMessageFailure(error.message));
          }
        );
      }
    },
    [dispatch, currentConversationId]
  );


  const startNewConversation = useCallback(() => {
    dispatch(setConversation(null));
    dispatch(setActiveConversation(null));
    dispatch(clearMessages());
  }, [dispatch]);

  const loadConversation = useCallback(
    (conversationId: string) => {
      dispatch(setLoading(true));
      dispatch(setConversation(conversationId));
      dispatch(setActiveConversation(conversationId));
      aiChatApiService.getConversationById(conversationId).then(
        (response: any) => {
         dispatch(loadExistingMessageSuccess(response));
         dispatch(setLoading(false));
        },
        (error:any) => {
          console.error(error);
          dispatch(setLoading(false));
          dispatch(setMessageFailure(error.message));
        }
      );
    },
    [dispatch]
  );

  const sendSuggestion = useCallback(
    (suggestion: string) => {
      sendChatMessage(suggestion);
    },
    [sendChatMessage]
  );

  const feedbackMessageRequest = useCallback(
    async (feedback: FeedbackRequest) => {
      dispatch(addMessageFeedback(feedback));
      aiChatApiService.addMessageFeedback(feedback).then(
        (response: any) => {
          if(response!=null){
            dispatch(addMessageFeedback(feedback));
          }
        },
        (error) => {
          console.error(error);
          dispatch(removeMessageFeedback(feedback));
        }
      );
    },
    [dispatch]
  );


  const getTableDataRequest = useCallback(
    async (table: TableRequest) => {
         dispatch(setLoading(true));
      aiChatApiService.getTablePaginationData(table).then(
        (response: any) => {
          if(response!=null){
           dispatch(setTablePaginatedDataSet(response));
          }
          dispatch(setLoading(false));
        },
        (error) => {
          console.error(error);
          dispatch(setLoading(false));
        }
      );
    },
    [dispatch]
  );

  const formatUtcForServerLikeName = (): string =>{
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    '_' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds())
  );
}

 const downloadTableAsPDF = useCallback(
  async (
    messageId: string,
    startDate?: string,
    toDate?: string,
    filterData?: FilterItem[]
  ) => {
    if (!messageId) {
      dispatch(setPdfDownloadError({ error: 'No message ID available for PDF download', messageId: messageId }));
      return;
    }

    dispatch(setPdfDownloadLoading(true));

    try {
      const pdfBlob = await aiChatApiService.downloadTableAsPDF(messageId, startDate, toDate, filterData);

      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;

      let filename = 'Account_Statement';

      if (startDate && toDate) {
        const cleanStart = startDate.replace(/-/g, '');
        const cleanEnd = toDate.replace(/-/g, '');
        filename += `_${cleanStart}_${cleanEnd}`;
      } else {
        filename += `_${formatUtcForServerLikeName()}`;
      }

      filename += '.pdf';

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      dispatch(setPdfDownloadLoading(false));
    } catch (error) {
      console.error('Error downloading PDF:', error);
      
      let userFriendlyMessage = 'Failed to generate PDF';
      
      if (error instanceof Error) {
        if (error.message.includes('500')) {
          userFriendlyMessage = 'Failed to generate PDF. Please try again later.';
        } else if (error.message.includes('404')) {
          userFriendlyMessage = 'PDF generation service not available.';
        } else if (error.message.includes('403')) {
          userFriendlyMessage = 'Access denied for PDF generation.';
        } else if (error.message.includes('timeout') || error.message.includes('time out')) {
          userFriendlyMessage = 'PDF generation is taking too long. Please try again.';
        } else if (error.message.includes('network') || error.message.includes('connection')) {
          userFriendlyMessage = 'Network error. Please check your connection and try again.';
        }
      }
      
      dispatch(setPdfDownloadError({ error: userFriendlyMessage, messageId: messageId }));
    }
  },
  [dispatch]
);

  const clearPDFDownloadError = useCallback(() => {
    dispatch(setPdfDownloadError({ error: null, messageId: null }));
  }, [dispatch]);

  // Handle different streaming response types
  const handleStreamingResponse = useCallback((response: StreamingResponse, conversationId: string | null, originalMessage?: string) => {
   // console.log('🔄 Handling streaming response:', response);
    
    switch (response.type) {
      case 'main_result':
        handleMainResultResponse(response as MainResultResponse, conversationId, originalMessage);
        break;
      case 'insight':
        handleInsightResponse(response as InsightResponse);
        break;
      case 'insights_complete':
        handleInsightsCompleteResponse(response as InsightsCompleteResponse);
        break;
      default:
        console.warn('Unknown response type:', response);
    }
  }, [dispatch]);

  const handleMainResultResponse = useCallback((response: MainResultResponse, conversationId: string | null, originalMessage?: string) => {
    // Create conversation if it doesn't exist
    if (!conversationId) {
      // Use the original message as the conversation title
      const conversationTitle = originalMessage || response.text || 'New Conversation';
      
      const conv: Conversation = {
        conversation_id: response.conversation_id,
        title: conversationTitle.length > 100 ? conversationTitle.substring(0, 100) + '...' : conversationTitle,
        updated_at: new Date().toISOString(),
        latest_message: {
          content: originalMessage || response.text || 'New query',
          role: 'user',
          created_at: new Date().toISOString(),
          sql_query: response.sql_query,
          feedback: null
        },
        created_at: new Date().toISOString()
      };
      dispatch(setNewConversation(conv));
    }
    
    // If this has visualization data, add it to visualizations array with proper order
    if (response.visualization && response.data && response.visualization !== 'text') {
      const vizData = {
        type: response.visualization.toLowerCase(),
        data: response.data,
        sql_query: response.sql_query,
        question: (response as any).question || (response as any).original_question || '',
        metadata: response.metadata,
        timestamp: Date.now(),
        report_suggestions: (response as any).report_suggestions && Array.isArray((response as any).report_suggestions) 
          ? (response as any).report_suggestions 
          : undefined
      };
      dispatch(addVisualizationResult(vizData));
    }
    
    // Convert to QueryResponse format for compatibility
    const queryResponse = {
      text: response.text,
      visualization: response.visualization,
      data: response.data,
      conversation_id: response.conversation_id,
      sql_query: response.sql_query,
      suggested_visualizations: [],
      suggested_questions: [],
      message_id: response.message_id,
      metadata: response.metadata,
      type: response.type,
      parent_message_id:response.parent_message_id,
      report_suggestions: (response as any).report_suggestions && Array.isArray((response as any).report_suggestions) 
        ? (response as any).report_suggestions 
        : undefined
    };
    
    dispatch(setMessageSuccess(queryResponse));
  }, [dispatch]);

  const handleInsightResponse = useCallback((response: InsightResponse) => {
    // Store insight for the same message_id
    if (response.result?.llm_insights?.insights) {
      // Dispatch action to add insight to the message
      dispatch(addInsightToMessage({
        messageId: response.message_id,
        insightType: response.insight_type,
        insight: response.result.llm_insights.insights,
        report_data: response.result.report_data || []
      }));
    }else if(response.result?.report_data && response.result.report_data.length > 0){
      dispatch(addInsightToMessage({
        messageId: response.message_id,
        insightType: response.insight_type,
        insight: response.result.llm_insights?.insights || '',
        report_data: response.result.report_data
      }));
    }


    
  }, [dispatch]);

  const handleInsightsCompleteResponse = useCallback((response: InsightsCompleteResponse) => {
   // console.log('✅ Insights completed:', response);
   // console.log(`Completed insights: ${response.completed_insights.join(', ')}`);
    //console.log(`Total time: ${response.total_time}`);
    
    // Stop loading when insights are complete
    dispatch(setLoading(false));
  }, [dispatch]);

  return {
    messages,
    pendingMessage,
    error,
    currentConversationId,
    activeConversationId,
    suggestions,
    suggestedVisualizations,
    suggestedQuestions,
    loading,
    isDownloadingPDF,
    pdfDownloadError,
    pdfDownloadErrorMessageId,
    sendChatMessage,
    startNewConversation,
    loadConversation,
    sendSuggestion,
    feedbackMessageRequest,
    getTableDataRequest,
    downloadTableAsPDF,
    clearPDFDownloadError
  };
};